<template>
    <Transition name="modal-fade">
        <div v-if="isVisible" class="cf-overlay" @click.self="close">
            <Transition name="modal-slide">
                <div v-if="isVisible" class="cf-dialog cf-dialog--lg" @click.stop>

                    <!-- ========== HEADER ========== -->
                    <div class="cf-modal-head cf-dialog-head">
                        <div class="cf-modal-head__main">
                            <span class="cf-modal-head__icon"><i class="ri-team-line"></i></span>
                            <div>
                                <h4 class="cf-dialog-title">{{ titleModal }}</h4>
                                <p class="cf-modal-head__sub">{{ dataTable.length }} élément{{ dataTable.length > 1 ? 's' : '' }}</p>
                            </div>
                        </div>
                        <button @click="close" type="button" class="cf-dialog-close" aria-label="Fermer"><i class="ri-close-line"></i></button>
                    </div>

                    <!-- ========== TOOLBAR ========== -->
                    <div class="cf-dialog-toolbar">
                        <div class="cf-toolbar-row">
                            <div class="search-wrap">
                                <i class="ri-search-line search-icon"></i>
                                <input v-model="searchQuery" type="search" class="search-input"
                                       :placeholder="searchPlaceholder" @keyup.enter="selectFirstResult">
                                <button v-if="searchQuery" @click="clearSearch" class="search-clear" aria-label="Effacer"><i class="ri-close-line"></i></button>
                            </div>
                            <button v-if="canAdd" @click="handleAdd" class="ti-btn ti-btn-primary-full">
                                <i class="ri-add-line align-middle"></i> {{ addButtonLabel }}
                            </button>
                        </div>
                        <div class="cf-toolbar-meta">
                            <p class="cf-count"><span>{{ filteredData.length }}</span> résultat{{ filteredData.length > 1 ? 's' : '' }}<template v-if="searchQuery"> pour « {{ searchQuery }} »</template></p>
                            <div class="cf-perpage">
                                <label>Afficher</label>
                                <div class="cf-select-wrap">
                                    <select v-model="perPage" @change="currentPage = 1" class="cf-inp cf-select cf-select--sm">
                                        <option v-for="option in [10, 20, 50, 100, 200]" :key="option" :value="option">{{ option }}</option>
                                    </select>
                                    <i class="ri-arrow-down-s-line cf-select-arrow"></i>
                                </div>
                                <span>par page</span>
                            </div>
                        </div>
                    </div>

                    <!-- ========== BODY ========== -->
                    <div class="cf-dialog-body cf-dialog-body--flush">
                        <!-- Desktop table -->
                        <div class="table-scroll-wrap tiers-scroll hidden lg:block">
                            <table class="tiers-table">
                                <thead>
                                    <tr>
                                        <th class="th" style="width:5rem">N°</th>
                                        <th class="th">{{ entityType === 'col' ? 'Nom / Type' : 'Raison sociale' }}</th>
                                        <th v-if="showExtraColumn" class="th">{{ extraColumnLabel }}</th>
                                        <th class="th th--center" style="width:9.5rem">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-if="paginatedData.length">
                                        <tr v-for="(item, index) in paginatedData" :key="item.pid || index"
                                            class="table-row cf-row-clickable"
                                            @dblclick="selectItem(item)">
                                            <td class="td row-number">{{ (currentPage - 1) * perPage + index + 1 }}</td>
                                            <td class="td">
                                                <div class="cf-tiers-cell">
                                                    <span class="cf-avatar-sm">{{ getInitials(item.name) }}</span>
                                                    <div class="cf-tiers-id">
                                                        <span class="cf-tiers-name">{{ item.name }}</span>
                                                        <span v-if="item.jsondata?.general?.telephone || item.telephone" class="cf-tiers-sub">{{ item.jsondata?.general?.telephone || item.telephone }}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td v-if="showExtraColumn" class="td">
                                                <span v-if="entityType === 'col'" class="cf-badge cf-badge--blue">{{ item.typecollaborateur || item.jsondata?.typecollaborateur || '-' }}</span>
                                                <span v-else class="cf-txt">{{ item.jsondata?.general?.adresse || item.adresse || '-' }}</span>
                                            </td>
                                            <td class="td td--center">
                                                <button @click.stop="selectItem(item)" class="cf-select-btn">
                                                    <i class="ri-check-line"></i> Sélectionner
                                                </button>
                                            </td>
                                        </tr>
                                    </template>
                                    <tr v-else>
                                        <td :colspan="showExtraColumn ? 4 : 3" class="td">
                                            <div class="empty-state">
                                                <div class="empty-state__icon"><i class="ri-team-line"></i></div>
                                                <p class="empty-state__title">Aucun résultat</p>
                                                <p class="empty-state__sub">{{ searchQuery ? 'Essayez de modifier votre recherche' : 'La liste est vide' }}</p>
                                                <button v-if="canAdd && !searchQuery" @click="handleAdd" class="ti-btn ti-btn-primary-full" style="margin-top:0.9rem">
                                                    <i class="ri-add-line align-middle"></i> {{ addButtonLabel }}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Mobile cards -->
                        <div class="lg:hidden tiers-scroll cf-cards">
                            <template v-if="paginatedData.length">
                                <div v-for="(item, index) in paginatedData" :key="item.pid || index"
                                     class="cf-card" @click="selectItem(item)">
                                    <div class="cf-card-head">
                                        <div class="cf-card-headmain">
                                            <span class="cf-card-index">{{ (currentPage - 1) * perPage + index + 1 }}</span>
                                            <span class="cf-card-title">{{ item.name }}</span>
                                        </div>
                                        <button @click.stop="selectItem(item)" class="cf-card-select" aria-label="Sélectionner"><i class="ri-check-line"></i></button>
                                    </div>
                                    <div class="cf-card-body" v-if="entityType === 'col' || item.jsondata?.general?.telephone || item.telephone || item.jsondata?.general?.adresse || item.adresse">
                                        <div v-if="entityType === 'col' && (item.typecollaborateur || item.jsondata?.typecollaborateur)">
                                            <p class="cf-card-lbl">Type</p>
                                            <span class="cf-badge cf-badge--blue">{{ item.typecollaborateur || item.jsondata?.typecollaborateur }}</span>
                                        </div>
                                        <div v-if="item.jsondata?.general?.telephone || item.telephone" class="cf-card-row">
                                            <p class="cf-card-lbl">Téléphone</p>
                                            <p class="cf-card-val">{{ item.jsondata?.general?.telephone || item.telephone }}</p>
                                        </div>
                                        <div v-if="item.jsondata?.general?.adresse || item.adresse" class="cf-card-row">
                                            <p class="cf-card-lbl">Adresse</p>
                                            <p class="cf-card-val">{{ item.jsondata?.general?.adresse || item.adresse }}</p>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <div v-else class="empty-state">
                                <div class="empty-state__icon"><i class="ri-team-line"></i></div>
                                <p class="empty-state__title">Aucun résultat</p>
                                <p class="empty-state__sub">{{ searchQuery ? 'Modifiez votre recherche' : 'La liste est vide' }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- ========== FOOTER ========== -->
                    <div class="cf-dialog-foot cf-dialog-foot--split">
                        <p class="cf-foot-info">Page {{ currentPage }} sur {{ totalPages || 1 }}</p>
                        <div class="cf-foot-actions">
                            <div class="pagination" v-if="totalPages > 1">
                                <button class="page-btn page-btn--nav" @click="currentPage--" :disabled="currentPage === 1"><i class="ri-arrow-left-s-line"></i></button>
                                <template v-for="pageNum in displayedPages" :key="pageNum">
                                    <span v-if="pageNum === '...'" class="cf-ellipsis">…</span>
                                    <button v-else class="page-btn" :class="{ 'page-btn--active': currentPage === pageNum }" @click="currentPage = pageNum">{{ pageNum }}</button>
                                </template>
                                <button class="page-btn page-btn--nav" @click="currentPage++" :disabled="currentPage >= totalPages"><i class="ri-arrow-right-s-line"></i></button>
                            </div>
                            <button @click="selectItem(null)" class="ti-btn ti-btn-light"><i class="ri-eraser-line align-middle"></i> Effacer</button>
                            <button @click="close" class="ti-btn ti-btn-secondary-full">Fermer</button>
                        </div>
                    </div>

                </div>
            </Transition>

            <!-- Child Modals -->
            <modalform ref="modalform" @closed="loadEntityData" />
            <modalformbeneficiaire ref="modalformbeneficiaire" @closed="loadBeneficiaryData" />
        </div>
    </Transition>
</template>

<script>
    const axios =require('axios');
    import { randomString } from '@/allfunction';
    import * as JwtService from '@/common/jwt.service';
    import modalform from '@/components/views/pages/gestioncommerciale/frscltcolb/form';
    import modalformbeneficiaire from '@/components/views/pages/caisse/beneficiaire/form';

    export default {
        /* eslint-disable */
        name: 'EntitySelectionModal',

        components: {
            modalform,
            modalformbeneficiaire,
        },

        data() {
            return {
                // Visibility
                isVisible: false,

                // Modal Data
                titleModal: '',
                entityType: '', // clt, frs, col, pers, vente, vvente, achat
                dataTable: [],
                otherData: [],
                inputLabels: [],
                customLabels: {},

                // Search & Pagination
                searchQuery: '',
                currentPage: 1,
                perPage: 50,
            };
        },

        computed: {
            // Type-based configuration
            typeConfig() {
                const configs = {
                    clt: {
                        iconBgClass: 'bg-blue-100 dark:bg-blue-900/30',
                        iconClass: 'text-blue-600 dark:text-blue-400',
                        avatarBgClass: 'bg-gradient-to-br from-blue-400 to-blue-600',
                        avatarTextClass: 'text-white',
                    },
                    vente: {
                        iconBgClass: 'bg-blue-100 dark:bg-blue-900/30',
                        iconClass: 'text-blue-600 dark:text-blue-400',
                        avatarBgClass: 'bg-gradient-to-br from-blue-400 to-blue-600',
                        avatarTextClass: 'text-white',
                    },
                    vvente: {
                        iconBgClass: 'bg-blue-100 dark:bg-blue-900/30',
                        iconClass: 'text-blue-600 dark:text-blue-400',
                        avatarBgClass: 'bg-gradient-to-br from-blue-400 to-blue-600',
                        avatarTextClass: 'text-white',
                    },
                    frs: {
                        iconBgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
                        iconClass: 'text-emerald-600 dark:text-emerald-400',
                        avatarBgClass: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
                        avatarTextClass: 'text-white',
                    },
                    achat: {
                        iconBgClass: 'bg-emerald-100 dark:bg-emerald-900/30',
                        iconClass: 'text-emerald-600 dark:text-emerald-400',
                        avatarBgClass: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
                        avatarTextClass: 'text-white',
                    },
                    col: {
                        iconBgClass: 'bg-purple-100 dark:bg-purple-900/30',
                        iconClass: 'text-purple-600 dark:text-purple-400',
                        avatarBgClass: 'bg-gradient-to-br from-purple-400 to-purple-600',
                        avatarTextClass: 'text-white',
                    },
                    pers: {
                        iconBgClass: 'bg-amber-100 dark:bg-amber-900/30',
                        iconClass: 'text-amber-600 dark:text-amber-400',
                        avatarBgClass: 'bg-gradient-to-br from-amber-400 to-amber-600',
                        avatarTextClass: 'text-white',
                    },
                };
                return configs[this.entityType] || configs.pers;
            },

            canAdd() {
                return this.entityType !== 'vvente';
            },

            addButtonLabel() {
                const labels = {
                    clt: 'Nouveau client',
                    vente: 'Nouveau client',
                    frs: 'Nouveau fournisseur',
                    achat: 'Nouveau fournisseur',
                    col: 'Nouveau collaborateur',
                    pers: 'Nouveau bénéficiaire',
                };
                return labels[this.entityType] || 'Ajouter';
            },

            searchPlaceholder() {
                const placeholders = {
                    clt: 'Rechercher un client...',
                    vente: 'Rechercher un client...',
                    vvente: 'Rechercher un client...',
                    frs: 'Rechercher un fournisseur...',
                    achat: 'Rechercher un fournisseur...',
                    col: 'Rechercher un collaborateur...',
                    pers: 'Rechercher un bénéficiaire...',
                };
                return placeholders[this.entityType] || 'Rechercher...';
            },

            showExtraColumn() {
                return ['col', 'clt', 'frs', 'vente', 'achat'].includes(this.entityType);
            },

            extraColumnLabel() {
                if (this.entityType === 'col') return 'Type';
                return 'Adresse';
            },

            filteredData() {
                let data = [...this.dataTable];

                if (this.searchQuery) {
                    const query = this.searchQuery.toLowerCase().trim();
                    data = data.filter(item => {
                        const name = item.name?.toLowerCase() || '';
                        const telephone = item.jsondata?.general?.telephone?.toLowerCase() || item.telephone?.toLowerCase() || '';
                        const address = item.jsondata?.general?.adresse?.toLowerCase() || item.adresse?.toLowerCase() || '';
                        return name.includes(query) || telephone.includes(query) || address.includes(query);
                    });
                }

                return data;
            },

            paginatedData() {
                const start = (this.currentPage - 1) * this.perPage;
                return this.filteredData.slice(start, start + this.perPage);
            },

            totalPages() {
                return Math.ceil(this.filteredData.length / this.perPage) || 1;
            },

            displayedPages() {
                const pages = [];
                const total = this.totalPages;
                const current = this.currentPage;

                if (total <= 5) {
                    for (let i = 1; i <= total; i++) pages.push(i);
                } else {
                    if (current <= 3) {
                        pages.push(1, 2, 3, '...', total);
                    } else if (current >= total - 2) {
                        pages.push(1, '...', total - 2, total - 1, total);
                    } else {
                        pages.push(1, '...', current, '...', total);
                    }
                }

                return pages;
            },
        },

        watch: {
            searchQuery() {
                this.currentPage = 1;
            },
        },

        methods: {
            // ============ MODAL METHODS ============
            show(title, listData, type, inputLabels, customLabels) {
                this.titleModal = title;
                this.dataTable = listData || [];
                this.entityType = type;
                this.inputLabels = inputLabels || [];
                this.customLabels = customLabels || {};
                this.searchQuery = '';
                this.currentPage = 1;

                this.loadOtherData();
                this.isVisible = true;
                document.body.style.overflow = 'hidden';
            },

            close() {
                this.isVisible = false;
                document.body.style.overflow = '';
            },

            // ============ SELECTION METHODS ============
            selectItem(item) {
                this.$emit('elementSelectionnecltfrs', {
                    typ: this.entityType,
                    value: item,
                    datatable: this.dataTable,
                });
                this.close();
            },

            selectFirstResult() {
                if (this.paginatedData.length > 0) {
                    this.selectItem(this.paginatedData[0]);
                }
            },

            clearSearch() {
                this.searchQuery = '';
            },

            // ============ ADD METHODS ============
            handleAdd() {
                const normalizedType = this.getNormalizedType();

                const emptyItem = {
                    pid: '',
                    codecltfrs: '',
                    codeclient: '',
                    raisonsociale: '',
                    representant: '',
                    typ: this.entityType,
                    jsondata: {
                        comptetva: null,
                        typecollaborateur: 'none',
                        general: { adresse: '', telephone: '', ifu: '' },
                        adresses: { info: '', info1: '', info2: '', info3: '' },
                        contacts: { info: '', info1: '', info2: '', info3: '' },
                        autreinfo: {},
                        paramdindication: { param: null, param1: null },
                    },
                    jsonother: {},
                };

                const titleMap = {
                    frs: 'Enregistrer un fournisseur',
                    clt: 'Enregistrer un client',
                    col: 'Enregistrer un collaborateur',
                    pers: 'Enregistrer un bénéficiaire',
                };

                if (['col', 'frs', 'clt'].includes(normalizedType)) {
                    this.$refs.modalform.show(
                        titleMap[normalizedType],
                        'add',
                        normalizedType,
                        emptyItem,
                        this.otherData,
                        this.inputLabels,
                        this.customLabels
                    );
                } else if (normalizedType === 'pers') {
                    const beneficiaryItem = { pid: '', representant: '', adresse: '', telephone: '' };
                    this.$refs.modalformbeneficiaire.show('Enregistrer un bénéficiaire', 'add', beneficiaryItem);
                }
            },

            getNormalizedType() {
                const typeMap = {
                    achat: 'frs',
                    vente: 'clt',
                    vvente: 'clt',
                };
                return typeMap[this.entityType] || this.entityType;
            },

            // ============ DATA LOADING ============
            async loadOtherData() {
                try {
                    const url = `${process.env.VUE_APP_API_URL}/ar85474524otherdatadofodfidfoioienf55485/ges/${this.$store.state.appsociete}/${randomString()}`;

                    const response = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });

                    if (response.data.success) {
                        this.otherData = response.data.otherdata || [];
                    }
                } catch (error) {
                    console.error('Error loading other data:', error);
                }
            },

            async loadEntityData() {
                try {
                    const url = `${process.env.VUE_APP_API_URL}/jkebfiuebffeznfoieznofibeubqsyudfyegl745874stock771714dfe/gescom/othetdskd8755dsdhisd/${this.entityType}/${this.$store.state.appsociete}/${randomString()}`;

                    const response = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });

                    if (response.data.success) {
                        this.dataTable = this.entityType === 'col'
                            ? response.data.otherdata.collaborateur || []
                            : response.data.otherdata.frsclt || [];
                    }
                } catch (error) {
                    console.error('Error loading entity data:', error);
                }
            },

            async loadBeneficiaryData() {
                try {
                    const url = `${process.env.VUE_APP_API_URL}/egl745874pdefinition771714dfe/caisse/othetdskd8755dsdhisd/other111dksdk/${this.$store.state.appsociete}/${randomString()}`;

                    const response = await axios.get(url, {
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                            'Accept': 'application/json',
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${JwtService.getToken()}`,
                            'csrftoken': this.$store.state.appsociete,
                        },
                        withCredentials: true,
                    });

                    if (response.data.success) {
                        this.dataTable = response.data.otherdata.beneficiaire || [];
                    }
                } catch (error) {
                    console.error('Error loading beneficiary data:', error);
                }
            },

            // ============ UTILITIES ============
            getInitials(name) {
                if (!name) return '?';
                return name
                    .split(' ')
                    .map(word => word[0])
                    .join('')
                    .substring(0, 2)
                    .toUpperCase();
            },
        },

        beforeUnmount() {
            document.body.style.overflow = '';
        },
    };
</script>

<style scoped>
/* Charte cf-/ds- : tokens uniquement (light-only) */
.cf-overlay { position:fixed; inset:0; z-index:1000; display:flex; align-items:center; justify-content:center; padding:1rem; background:rgb(15 23 42 / 0.45); }
.cf-dialog { width:100%; background:#fff; border-radius:var(--cf-radius); box-shadow:var(--cf-shadow-md); display:flex; flex-direction:column; max-height:92vh; overflow:hidden; }
.cf-dialog--lg { max-width:880px; }
.cf-dialog-head { display:flex; align-items:center; justify-content:space-between; padding:1rem 1.25rem; border-bottom:1px solid var(--cf-border); flex-shrink:0; }
.cf-dialog-title { margin:0; font-size:0.95rem; font-weight:700; color:var(--cf-ink-strong); }
.cf-dialog-close { height:2rem; width:2rem; flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; border-radius:var(--cf-radius-sm); border:none; background:var(--cf-surface-alt); color:var(--cf-muted); cursor:pointer; transition:all .15s; }
.cf-dialog-close:hover { background:#fee2e2; color:rgb(var(--danger)); }
.cf-dialog-toolbar { padding:0.75rem 1.25rem; border-bottom:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-body { padding:1.1rem 1.25rem; overflow-y:auto; flex:1; }
.cf-dialog-body--flush { padding:0; }
.cf-dialog-foot { display:flex; align-items:center; justify-content:flex-end; gap:0.6rem; padding:0.9rem 1.25rem; border-top:1px solid var(--cf-border); background:var(--cf-surface); flex-shrink:0; }
.cf-dialog-foot--split { justify-content:space-between; flex-wrap:wrap; }

/* Toolbar */
.cf-toolbar-row { display:flex; gap:0.75rem; align-items:center; }
.search-wrap { position:relative; flex:1; }
.search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); color:var(--cf-faint); }
.search-input { width:100%; padding:9px 34px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); font-size:0.82rem; color:var(--cf-ink); outline:none; transition:border-color 0.2s; }
.search-input:focus { border-color:rgb(var(--primary)); box-shadow:0 0 0 3px rgb(var(--primary) / 0.10); }
.search-clear { position:absolute; right:8px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; color:var(--cf-faint); }
.cf-toolbar-meta { display:flex; align-items:center; justify-content:space-between; margin-top:0.7rem; gap:0.75rem; }
.cf-count { font-size:0.8rem; color:var(--cf-muted); margin:0; }
.cf-count span { color:var(--cf-ink-strong); font-weight:600; }
.cf-perpage { display:flex; align-items:center; gap:0.4rem; font-size:0.8rem; color:var(--cf-muted); }
.cf-select-wrap { position:relative; }
.cf-select { appearance:none; padding-right:28px; cursor:pointer; }
.cf-select--sm { height:34px; padding:0 28px 0 10px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); background:#fff; font-size:0.8rem; color:var(--cf-ink); }
.cf-select-arrow { position:absolute; right:10px; top:50%; transform:translateY(-50%); color:var(--cf-faint); pointer-events:none; }

/* Table */
.table-scroll-wrap { overflow:auto; }
.tiers-scroll { max-height:52vh; }
.tiers-table { width:100%; text-align:left; font-size:13px; border-collapse:collapse; min-width:480px; }
.th { position:sticky; top:0; z-index:2; padding:0.7rem 1rem; font-weight:600; font-size:0.75rem; color:var(--cf-ink); background:var(--cf-surface-alt); border-bottom:1px solid var(--cf-border); white-space:nowrap; letter-spacing:0.02em; text-align:left; }
.th--center { text-align:center; }
.table-row { border-bottom:1px solid var(--cf-surface-alt); transition:background-color 0.15s; }
.table-row:hover { background:var(--cf-surface); }
.cf-row-clickable { cursor:pointer; }
.td { padding:0.65rem 1rem; font-size:0.82rem; color:var(--cf-ink); vertical-align:middle; }
.td--center { text-align:center; }
.row-number { color:var(--cf-faint); font-size:0.75rem; font-variant-numeric:tabular-nums; }

/* Cellule tiers */
.cf-tiers-cell { display:flex; align-items:center; gap:0.7rem; min-width:0; }
.cf-avatar-sm { flex-shrink:0; width:34px; height:34px; display:inline-flex; align-items:center; justify-content:center; border-radius:999px; background:rgb(var(--primary) / 0.12); color:rgb(var(--primary)); font-size:0.78rem; font-weight:700; }
.cf-tiers-id { display:flex; flex-direction:column; min-width:0; }
.cf-tiers-name { font-weight:500; color:var(--cf-ink-strong); }
.cf-tiers-sub { font-size:0.75rem; color:var(--cf-muted); }
.cf-txt { color:var(--cf-muted); }

/* Badge type collaborateur */
.cf-badge { display:inline-flex; align-items:center; padding:2px 9px; font-size:0.72rem; font-weight:600; border-radius:999px; white-space:nowrap; }
.cf-badge--blue { background:#dbeafe; color:#1e40af; }

/* Bouton Sélectionner */
.cf-select-btn { display:inline-flex; align-items:center; gap:0.3rem; padding:5px 12px; font-size:0.78rem; font-weight:600; border:none; border-radius:var(--cf-radius-sm); background:rgb(var(--primary) / 0.10); color:rgb(var(--primary)); cursor:pointer; transition:all 0.15s; white-space:nowrap; }
.cf-select-btn:hover { background:rgb(var(--primary)); color:#fff; }

/* Empty state */
.empty-state { padding:2.5rem 1.5rem; text-align:center; }
.empty-state__icon { font-size:40px; color:var(--cf-faint); margin-bottom:14px; }
.empty-state__title { font-size:0.95rem; font-weight:600; color:var(--cf-ink-strong); margin:0; }
.empty-state__sub { font-size:0.8rem; color:var(--cf-muted); margin:4px 0 0; }

/* Mobile cards */
.cf-cards { padding:0.75rem; display:flex; flex-direction:column; gap:0.5rem; }
.cf-card { border:1px solid var(--cf-border); border-radius:var(--cf-radius-sm); overflow:hidden; background:#fff; cursor:pointer; }
.cf-card-head { display:flex; align-items:center; justify-content:space-between; padding:0.6rem 0.85rem; background:var(--cf-surface-alt); border-bottom:1px solid var(--cf-border); }
.cf-card-headmain { display:flex; align-items:center; gap:0.5rem; min-width:0; }
.cf-card-index { flex-shrink:0; display:inline-flex; align-items:center; justify-content:center; min-width:1.5rem; height:1.5rem; padding:0 5px; font-size:0.72rem; font-weight:700; color:#fff; background:rgb(var(--primary)); border-radius:999px; }
.cf-card-title { font-weight:600; color:var(--cf-ink-strong); font-size:0.85rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.cf-card-select { flex-shrink:0; height:2rem; width:2rem; display:inline-flex; align-items:center; justify-content:center; border:none; background:rgb(var(--primary) / 0.10); color:rgb(var(--primary)); border-radius:var(--cf-radius-sm); cursor:pointer; }
.cf-card-body { padding:0.75rem 0.85rem; }
.cf-card-row { margin-top:0.6rem; }
.cf-card-lbl { font-size:0.7rem; text-transform:uppercase; letter-spacing:0.03em; color:var(--cf-muted); margin:0 0 4px; }
.cf-card-val { font-size:0.82rem; color:var(--cf-ink); margin:0; }

/* Footer */
.cf-foot-info { font-size:0.78rem; color:var(--cf-muted); margin:0; }
.cf-foot-actions { display:flex; align-items:center; gap:0.5rem; flex-wrap:wrap; justify-content:flex-end; }
.pagination { display:flex; align-items:center; gap:4px; }
.cf-ellipsis { padding:0 4px; color:var(--cf-faint); }
.page-btn { min-width:30px; height:30px; padding:0 8px; border:1px solid rgb(var(--input-border,203 213 225)); border-radius:var(--cf-radius-sm); background:#fff; color:var(--cf-ink); font-size:0.78rem; font-weight:600; cursor:pointer; transition:all 0.15s; }
.page-btn:hover:not(:disabled) { border-color:rgb(var(--primary) / 0.35); background:var(--cf-surface); }
.page-btn:disabled { opacity:0.5; cursor:not-allowed; }
.page-btn--active { background:rgb(var(--primary)); border-color:rgb(var(--primary)); color:#fff; }
.page-btn--nav { width:34px; display:inline-flex; align-items:center; justify-content:center; }

/* Transitions */
.modal-fade-enter-active, .modal-fade-leave-active { transition:opacity 0.25s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity:0; }
.modal-slide-enter-active, .modal-slide-leave-active { transition:all 0.25s ease; }
.modal-slide-enter-from, .modal-slide-leave-to { opacity:0; transform:translateY(-16px); }

.tiers-scroll::-webkit-scrollbar { width:6px; height:6px; }
.tiers-scroll::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:3px; }

@media (max-width:768px) { .cf-dialog--lg { max-width:100%; } .tiers-scroll { max-height:60vh; } }
</style>
